import React from 'react';
import { Box, Avatar, Typography, Link as MuiLink } from '@mui/material';
import { Favorite as FavoriteIcon, Circle as CircleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

export default function LikeNotificationCard({ notification, onMarkRead }) {
  const navigate = useNavigate();
  const payload = notification.payload || {};
  const theme = useTheme();
  // console.log('LikeNotificationCard payload:', payload);

  const getContentUrl = () => {
    if (!payload.content_type || !payload.content_id) return null;
    
    const contentType = payload.content_type.toLowerCase();
    const objectId = payload.content_id;

    switch (contentType) {
      case 'post':
        // Use /feed/:profileId/posts/:postId route if we have the author ID
        if (payload.post_author_id) {
          return `/feed/${payload.post_author_id}/posts/${objectId}`;
        }
        // Fallback to just the feed if author ID is missing
        return `/feed/${payload.liker?.id}/posts/${objectId}`;
      case 'reflection':
        return `/reflections/${objectId}`;
      case 'call':
        return `/funding/calls/${objectId}`;
      case 'consortium':
        return `/consortium/${objectId}`;
      case 'suggestion':
        return `/suggestions/`;
      case 'notification':
        return '/notifications';
      case 'comment':
        // For comments, navigate to the comment's target with a comment hash
        if (payload.comment_target_type && payload.comment_target_id) {
          const targetType = payload.comment_target_type.toLowerCase();
          const targetId = payload.comment_target_id;
          
          switch (targetType) {
            case 'reflection':
              // For reflection comments, use the full profile path if available
              if (payload.reflection_author_id && payload.reflection_content_type && payload.reflection_object_id) {
                return `/profile/${payload.reflection_author_id}/reflections/${payload.reflection_content_type}/${payload.reflection_object_id}#comment-${objectId}`;
              }
              return `/reflections/${targetId}#comment-${objectId}`;
            case 'call':
              return `/funding/call/${targetId}#comment-${objectId}`;
            case 'consortium':
              return `/consortium/${targetId}#comment-${objectId}`;
            default:
              return null;
          }
        }
        return null;
      default:
        return null;
    }
  };

  // Get the display name for the content type
  const getContentDisplayName = () => {
    const contentType = payload.content_type?.toLowerCase();
    
    // If it's a reflection, use the specific reflection_content_type if available
    if (contentType === 'reflection' && payload.reflection_content_type) {
      return payload.reflection_content_type;
    }
    
    // If it's a comment, show "comment"
    if (contentType === 'comment') {
      return 'comment';
    }
    
    return payload.content_type || 'post';
  };

  const contentUrl = getContentUrl();
  const contentDisplayName = getContentDisplayName();
  const likerProfileUrl = payload.liker?.id ? `/profile/${payload.liker.id}` : null;
  const likerName = (payload.liker?.name || '').trim() || 'Someone';



  const handleMarkRead = () => {
    if (!notification.is_read && onMarkRead) {
      onMarkRead();
    }
  };

  const handleLikerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleMarkRead();
    if (likerProfileUrl) {
      navigate(likerProfileUrl);
    }
  };

  const handleCardClick = () => {
    handleMarkRead();
    if (contentUrl) {
      navigate(contentUrl);
    }
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        transition: 'background-color 0.2s',
        cursor: contentUrl ? 'pointer' : 'default',
        '&:hover': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      {/* Avatar */}
      <Avatar
        src={payload.liker?.avatar}
        alt={payload.liker?.name || 'User'}
        sx={{
          width: 32,
          height: 32,
        //   bgcolor: 'error.main',
          cursor: likerProfileUrl ? 'pointer' : 'default',

          backgroundColor: theme.palette.primary.avatar,
          color: 'white',
        }}
        onClick={likerProfileUrl ? handleLikerClick : undefined}
      >
        {likerName !== 'Someone' ? likerName.charAt(0) : <FavoriteIcon fontSize="small" />}
      </Avatar>

      {/* Single line text */}
      <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {likerProfileUrl ? (
          <MuiLink
            component="a"
            href={likerProfileUrl}
            underline="hover"
            color="text.primary"
            fontWeight={600}
            onClick={handleLikerClick}
            sx={{ cursor: 'pointer' }}
          >
            {likerName}
          </MuiLink>
        ) : (
          <Box component="span" fontWeight={600}>
            {likerName}
          </Box>
        )}
        {payload.reaction_type && (
          <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>
            {payload.reaction_type}
          </Box>
        )}
        <Box component="span" color="text.secondary">
          liked your {contentDisplayName}
        </Box>
        {payload.body_preview && (
          <>
            <Box component="span" color="text.secondary">:</Box>
            <Box component="span" color="text.secondary" fontStyle="italic" sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '200px',
            }}>
              "{payload.body_preview}"
            </Box>
          </>
        )}
      </Typography>

      {/* Timestamp and unread indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && (
          <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
        )}
      </Box>
    </Box>
  );
}
